import React, { useState } from "react";
import "./TeacherCard.css";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SchoolIcon from '@mui/icons-material/School';
import BlockIcon from "@mui/icons-material/Block";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationModal from "../../../common/ConfirmationModal/ConfirmationModal";
import useAxios from "../../../../hooks/useAxios";

function TeacherCard({ teacher, onActionComplete }) {
  const axiosInstance = useAxios();
  const [action, setAction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);

  const {
    full_name,
    user: { email, phone_number, username, status, id: teacherId },
    number_of_students,
    number_of_classes,
    quizzes_count,
    classes_count,
    completed_sessions_count,
    students_added_count,
  } = teacher;

  const openModal = (actionType) => {
    setAction(actionType);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAction(null);
  };

  const handleAction = async () => {
    setLoadingAction(true);
    try {
      let endpoint = "";
      if (action === "suspend") endpoint = `/school/teachers/${teacherId}/suspend`;
      if (action === "reactivate") endpoint = `/school/teachers/${teacherId}/reactivate`;
      if (action === "remove") endpoint = `/school/teachers/${teacherId}/remove`;

      const response = await axiosInstance.post(endpoint);
      if (response.data.success) {
        onActionComplete();
        handleCloseModal();
      }
    } catch (err) {
      console.error("Action failed:", err.response?.data?.message || err.message);
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <Box className="teacher-card">
        {/* Status Badge */}
        {/* <Chip
            label={status.toUpperCase()}
            className={`status-badge ${status}`} // Adds dynamic class based on status
            size="small"
        /> */}

      {/* Header */}
        <Box className="teacher-header">
        <Avatar className="teacher-avatar">{full_name.charAt(0)}</Avatar>
        <Box className="teacher-info">
            <Typography variant="h6" className="teacher-name" title={full_name}>
            {full_name.length > 18 ? `${full_name.slice(0, 18)}...` : full_name}
            </Typography>
            <Box className="teacher-status-container">
            <Chip
                label={status.charAt(0).toUpperCase() + status.slice(1)} // Capitalize first letter
                className={`status-chip ${status}`}
                size="small"
            />
            <Typography variant="subtitle2" className="teacher-username">
                @{username}
            </Typography>
            </Box>
        </Box>
        </Box>


      <Divider />

      {/* Statistics */}
      <Box className="teacher-stats-grid">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box className="stat-item">
              <AccountBalanceIcon className="stat-icon" />
              <Typography>{classes_count || 0} Classes</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box className="stat-item">
              <QuestionAnswerIcon className="stat-icon" />
              <Typography>{quizzes_count || 0} Quizzes</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box className="stat-item">
              <SchoolIcon className="stat-icon" />
              <Typography>{completed_sessions_count || 0} Sessions</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box className="stat-item">
              <GroupsIcon className="stat-icon" />
              <Typography>{students_added_count || 0} Students</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* Footer: Contact & Actions */}
      <Box className="teacher-footer">
        {/* Contact */}
        <Box className="contact-actions">
          <Tooltip title={`Email: ${email}`}>
            <IconButton component="a" href={`mailto:${email}`} className="primary-icon">
              <EmailIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={`Phone: ${phone_number}`}>
            <IconButton component="a" href={`tel:${phone_number}`} className="primary-icon">
              <PhoneIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Management Actions */}
        <Box className="management-actions">
          {status === "active" && (
            <Tooltip title="Suspend Teacher">
              <IconButton onClick={() => openModal("suspend")} color="warning" className="orange-icon">
                <BlockIcon />
              </IconButton>
            </Tooltip>
          )}

          {status === "suspended" && (
            <Tooltip title="Reactivate Teacher">
              <IconButton onClick={() => openModal("reactivate")} color="success">
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Remove Teacher">
            <IconButton onClick={() => openModal("remove")} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        title={`Confirm ${action === "remove" ? "Removal" : action === "suspend" ? "Suspension" : "Reactivation"}`}
        subtitle={`Are you sure you want to ${
          action === "remove" ? "remove" : action === "suspend" ? "suspend" : "reactivate"
        } this teacher? ${
          action === "remove" ? "This action cannot be undone." : ""
        }`}
        onSubmit={handleAction}
        isDanger={action === "remove"}
      />
    </Box>
  );
}

export default TeacherCard;
